/**
 * URL detection and linkify helpers for chat messages.
 * Supports https://, http://, www., and bare domains (example.com).
 * Invalid candidates stay plain text.
 */

const TRAILING_PUNCT_RE = /[),.!?;:'"\]]+$/;

// Captures possible URL-like tokens; each match is validated before linkifying.
const URL_CANDIDATE_RE =
  /(?:https?:\/\/|www\.)[^\s<>"'{}|\\^`[\]]+|\b(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}(?:\/[^\s<>"'{}|\\^`[\]]*)?/gi;

const PRIVATE_HOST_RE =
  /^(localhost|127\.|10\.|192\.168\.|172\.(1[6-9]|2\d|3[0-1])\.|0\.0\.0\.0|::1|\[::1\])/i;

const isEmailContext = (text, index) => index > 0 && text[index - 1] === "@";

export function stripTrailingPunctuation(raw = "") {
  let value = String(raw);
  while (TRAILING_PUNCT_RE.test(value)) {
    value = value.replace(TRAILING_PUNCT_RE, "");
  }
  return value;
}

export function normalizeUrl(raw) {
  const cleaned = stripTrailingPunctuation(String(raw || "").trim());
  if (!cleaned) return "";
  if (/^https?:\/\//i.test(cleaned)) return cleaned;
  return `https://${cleaned}`;
}

export function isValidHttpUrl(raw) {
  try {
    const normalized = normalizeUrl(raw);
    if (!normalized) return false;
    const url = new URL(normalized);
    if (url.protocol !== "http:" && url.protocol !== "https:") return false;
    const host = url.hostname;
    if (!host) return false;
    if (host === "localhost") return true;
    if (!host.includes(".")) return false;
    if (!/^[a-z0-9.-]+$/i.test(host)) return false;
    // Require a plausible TLD (2+ letters)
    const parts = host.split(".");
    const tld = parts[parts.length - 1];
    if (!/^[a-z]{2,}$/i.test(tld)) return false;
    return true;
  } catch {
    return false;
  }
}

export function getUrlDomain(raw) {
  try {
    return new URL(normalizeUrl(raw)).hostname.replace(/^www\./i, "");
  } catch {
    return "";
  }
}

export function extractUrls(text) {
  if (!text || typeof text !== "string") return [];
  const found = [];
  const seen = new Set();
  const re = new RegExp(URL_CANDIDATE_RE.source, URL_CANDIDATE_RE.flags);
  let match;
  while ((match = re.exec(text)) !== null) {
    if (isEmailContext(text, match.index)) continue;
    const raw = stripTrailingPunctuation(match[0]);
    if (!isValidHttpUrl(raw)) continue;
    const href = normalizeUrl(raw);
    if (seen.has(href)) continue;
    seen.add(href);
    found.push({ raw, href, index: match.index });
  }
  return found;
}

/**
 * Split text into plain / link segments for React rendering.
 * @returns {Array<{ type: 'text'|'link', value: string, href?: string }>}
 */
export function linkifySegments(text) {
  if (!text || typeof text !== "string") {
    return [{ type: "text", value: text || "" }];
  }

  const re = new RegExp(URL_CANDIDATE_RE.source, URL_CANDIDATE_RE.flags);
  const segments = [];
  let lastIndex = 0;
  let match;

  while ((match = re.exec(text)) !== null) {
    const fullMatch = match[0];
    const start = match.index;
    const raw = stripTrailingPunctuation(fullMatch);
    const trailing = fullMatch.slice(raw.length);

    if (start > lastIndex) {
      segments.push({ type: "text", value: text.slice(lastIndex, start) });
    }

    if (!isEmailContext(text, start) && isValidHttpUrl(raw)) {
      segments.push({
        type: "link",
        value: raw,
        href: normalizeUrl(raw),
      });
      if (trailing) {
        segments.push({ type: "text", value: trailing });
      }
    } else {
      segments.push({ type: "text", value: fullMatch });
    }

    lastIndex = start + fullMatch.length;
  }

  if (lastIndex < text.length) {
    segments.push({ type: "text", value: text.slice(lastIndex) });
  }

  return segments.length ? segments : [{ type: "text", value: text }];
}

export function isPrivateOrBlockedHost(hostname) {
  if (!hostname) return true;
  return PRIVATE_HOST_RE.test(hostname);
}
